
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/auth/AuthContext';
import { useToast } from '@/hooks/use-toast';
import {
  ProfileUpdateData,
  NotificationPreferences as NotificationPrefsType,
  CompanyInfo
} from '@/contexts/auth/types';
import { getUserProfile } from '@/contexts/auth/services/profileService';

export const useOrganizerProfile = () => {
  const { user, updateProfile, token } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [profileId, setProfileId] = useState<string | null>(null);

  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    academicTitle: '',
    formalSalutation: '',
    email: '',
    contactPhone: '',
    website: '',
  });

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (token) {
        setIsLoadingProfile(true);
        try {
          const response = await getUserProfile(token);
          console.log('Fetched user profile:', response);

          if (response.success && response.user) {
            const fetchedUser = response.user;

            console.log('Company Info in User Profile:', fetchedUser.userProfile.companyInfo);

            // Store profile ID if available
            if (fetchedUser.userProfile && fetchedUser.userProfile['@id']) {
              const id = fetchedUser.userProfile['@id'];
              console.log('User Profile @id:', id);
              setProfileId(id);
              // Backup storage in localStorage for robustness
              localStorage.setItem('userProfileId', id);
            } else {
              console.warn('User profile fetched but @id is missing:', fetchedUser.userProfile);
            }

            setProfileData(prevData => ({
              ...prevData,
              firstName: fetchedUser.userProfile.name.firstName || '',
              lastName: fetchedUser.userProfile.name.lastName || '',
              academicTitle: fetchedUser.userProfile.name.academicTitle || '',
              formalSalutation: fetchedUser.userProfile.name.formalSalutation || '',
              email: fetchedUser.email || '',
              contactPhone: fetchedUser.userProfile.contactPhone || '',
              website: fetchedUser.userProfile.website || '',
            }));
          } else {
            toast({
              title: "Profil betöltési hiba",
              description: response.message || "Nem sikerült betölteni a profil adatokat.",
              variant: "destructive",
            });
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
          toast({
            title: "Profil betöltési hiba",
            description: "Váratlan hiba történt a profil betöltése során.",
            variant: "destructive",
          });
        } finally {
          setIsLoadingProfile(false);
        }
      } else {
        setIsLoadingProfile(false);
      }
    };

    fetchUserProfile();
  }, [token, toast]);

  useEffect(() => {
    if (user && !isLoadingProfile) {
      const shouldUpdate = profileData.firstName === '' || profileData.lastName === '';

      if (shouldUpdate) {
        setProfileData(prevData => ({
          ...prevData,
          firstName: user.userProfile.name.firstName || prevData.firstName,
          lastName: user.userProfile.name.lastName || prevData.lastName,
          academicTitle: user.userProfile.name.academicTitle || prevData.academicTitle,
          formalSalutation: user.userProfile.name.formalSalutation || prevData.formalSalutation,
          email: user.email || prevData.email,
          contactPhone: user.userProfile?.contactPhone || prevData.contactPhone,
          website: user.userProfile?.website || prevData.website,
        }));
      }

      // Check for profile ID from user object
      if (user.userProfile && user.userProfile['@id'] && !profileId) {
        setProfileId(user.userProfile['@id']);
        localStorage.setItem('userProfileId', user.userProfile['@id']);
      }
    }
  }, [user, isLoadingProfile, profileData.firstName, profileData.lastName, profileId]);

  const handleProfileSubmit = async (updatedProfileData: ProfileUpdateData) => {
    setIsLoading(true);

    try {
      if (user?.['@id'] === 'test-user-id') {
        toast({
          title: "Profil frissítve (Teszt módban)",
          description: "A profil adatok frissítése sikeres lenne éles környezetben.",
        });
        setIsLoading(false);
        return;
      }

      if (updateProfile) {
        const success = await updateProfile(updatedProfileData);
        if (success) {
          toast({
            title: "Sikeres frissítés",
            description: "Profil adatok sikeresen frissítve.",
          });
        }
      } else {
        toast({
          title: "Profil frissítve",
          description: "A profil adatok frissítése sikeres.",
        });
      }
    } catch (error) {
      console.error('Profile update error:', error);
      toast({
        title: "Hiba történt",
        description: "Nem sikerült frissíteni a profil adatokat.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompanyInfoSubmit = async (companyInfo: CompanyInfo) => {
    setIsLoading(true);

    try {
      if (user?.['@id'] === 'test-user-id') {
        toast({
          title: "Céges adatok frissítve (Teszt módban)",
          description: "A céges adatok frissítése sikeres lenne éles környezetben.",
        });
        setIsLoading(false);
        return;
      }

      console.log('Submitting company info:', companyInfo);

      // Try to get the profile ID from multiple sources
      const userProfileId = profileId ||
        (user?.userProfile && user.userProfile['@id']) ||
        localStorage.getItem('userProfileId');

      if (!userProfileId) {
        console.error('Missing user profile ID. Checked:',
          'profileId:', profileId,
          'user.userProfile?.@id:', user?.userProfile?.['@id'],
          'localStorage:', localStorage.getItem('userProfileId')
        );
        toast({
          title: "Hiba",
          description: "A felhasználói profil azonosítója hiányzik.",
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      console.log('Using profile ID for company info update:', userProfileId);

      const profileUpdateData: ProfileUpdateData = {
        owner: 'me',
        name: user?.userProfile?.name || { firstName: '', lastName: '' },
        email: user?.email || '',
        companyInfo: companyInfo
      };

      if (updateProfile) {
        const success = await updateProfile(profileUpdateData);
        if (success) {
          toast({
            title: "Sikeres frissítés",
            description: "Céges adatok sikeresen frissítve.",
          });
        }
      } else {
        toast({
          title: "Céges adatok frissítve",
          description: "A céges adatok frissítése sikeres.",
        });
      }
    } catch (error) {
      console.error('Company info update error:', error);
      toast({
        title: "Hiba történt",
        description: "Nem sikerült frissíteni a céges adatokat.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    profileData,
    isLoading,
    isLoadingProfile,
    handleProfileSubmit,
    handleCompanyInfoSubmit,
    user,
    profileId
  };
};
